import { match } from "ts-pattern";
import type { Promisable, Simplify } from "type-fest";

import type {
  AllocateResponsibleUserRequest,
  Assessment,
  CurrentUser,
  EndInvolvementsRequest,
  FinishedAssessment,
  FoundUser,
  GuestOnCase,
  Organisation,
  OrganisationId,
  OrganisationType,
  Person,
  PersonAction,
  PersonId,
  PersonInvolvement,
  ReplaceResponsibleUserRequest,
  StartInvolvementsRequest,
  UserId,
} from "@thalamos/haven-common";
import {
  approverPartyForAssessor,
  customerRolesOf,
  isAssessmentApprover,
  isAssessmentOwner,
  isAssessmentStatusUnderWay,
  isPartyDesignatedAssessor,
  mayDeleteAssessment,
  mayReadAssessmentContents,
  mayReadGuestDetails,
  organisationsNearPerson,
  personActionsFromOwnInvolvements,
  personActionsNarrowedByAllocation,
  personActionsOf,
} from "@thalamos/haven-common";

import type { FinishedAssessmentActions } from "../../components/AssessmentHistory/finished_assessment_row.js";
import type { CaseInvolvementActor } from "../../components/CaseInvolvement/CaseInvolvement.js";
import type {
  CurrentAssessmentActions,
  NoActionReason,
} from "../../components/CurrentAssessments/CurrentAssessments.js";
import type {
  InvolvementOutcome,
  PartySearch,
} from "../../components/InvolvementDialogs/InvolvementDialogs.js";
import { writeSucceeded } from "./writeSucceeded.js";

/**
 * Changing who is involved, for a reader who may. Absent for everybody else,
 * which is what makes the profile read-only by default; see `canAssign`.
 */
export type InvolvementManagement = PartySearch & {
  onAdd: (request: StartInvolvementsRequest) => Promisable<InvolvementOutcome>;
  onEnd: (request: EndInvolvementsRequest) => Promisable<InvolvementOutcome>;
  onReplaceResponsibleUser: (
    request: ReplaceResponsibleUserRequest,
  ) => Promisable<InvolvementOutcome>;
  /** Reads who is involved again, after a change was refused as stale. */
  onRefresh: () => Promisable<void>;
};

/** How many candidates a picker asks for at a time. */
const PICKER_PAGE = 25;

/**
 * The party this session acts as: who, for which organisation, in which
 * customer roles. Undefined for a session with no context chosen.
 */
const actingParty = (user: CurrentUser | undefined) => {
  const acting = user?.sessionContext;

  if (user === undefined || acting === null || acting === undefined) {
    return undefined;
  }

  return {
    userId: user.id,
    organisationId: acting.organisationId,
    // Only customer roles convey anything.
    roles: customerRolesOf(acting.roles),
    memberOrganisationIds: acting.memberOrganisationIds,
    readsApprovedAssessmentsThroughMembers:
      acting.readsApprovedAssessmentsThroughMembers,
  };
};

/**
 * Whether the session is an assessment's owner, as `isAssessmentOwner` has it
 * for the server's gate: false while it acts for any other organisation than
 * the one the assessment was started for.
 */
export const sessionOwnsAssessment = ({
  user,
  assessment,
}: {
  user: CurrentUser | undefined;
  assessment: Pick<Assessment, "ownerUserId" | "organisationId">;
}): boolean => {
  const party = actingParty(user);

  return party !== undefined && isAssessmentOwner({ assessment, party });
};

/**
 * What this session may ask of this young person's record, read off
 * `personActionsOf`. Not off the role: reach over a young person is what is
 * recorded about them, and no role reaches every child by being held.
 *
 * Empty for a session with no context chosen, which is the same answer as an
 * involvement conveying nothing. Without `person`, only the involvements are
 * counted, narrowed by the record; registering, administering and the actions
 * derived from the record all need it.
 */
const sessionConveys = (
  user: CurrentUser | undefined,
  involvements: readonly PersonInvolvement[] | undefined,
  person: Person | undefined,
): readonly PersonAction[] => {
  const party = actingParty(user);

  if (party === undefined) {
    return [];
  }

  return person === undefined
    ? personActionsNarrowedByAllocation(
        personActionsFromOwnInvolvements(involvements ?? [], party),
        involvements ?? [],
      )
    : personActionsOf(person, involvements ?? [], party);
};

/**
 * Whether the session may archive and unarchive this profile. False until the
 * record and its involvements have both loaded.
 */
export const canArchive = (
  user: CurrentUser | undefined,
  involvements: PersonInvolvement[] | undefined,
  person: Person | undefined,
): boolean =>
  involvements !== undefined &&
  person !== undefined &&
  sessionConveys(user, involvements, person).includes("archive");

/**
 * The actor `CaseInvolvement` offers changes to, from what the session conveys
 * over this record. Undefined for a reader.
 */
export const caseInvolvementActor = (
  user: CurrentUser | undefined,
  involvements: PersonInvolvement[] | undefined,
  person?: Person,
): CaseInvolvementActor | undefined =>
  actorConveyed(sessionConveys(user, involvements, person));

/**
 * The actor `CaseInvolvement` offers changes to, given what is conveyed:
 * whoever may decide who is involved runs the case, and otherwise the body that
 * opened a record nobody runs may name who does. Undefined for a reader.
 */
export const actorConveyed = (
  conveyed: readonly PersonAction[],
): CaseInvolvementActor | undefined => {
  if (conveyed.includes("assign")) {
    return "responsible_user";
  }

  return conveyed.includes("allocate_responsible_user")
    ? "created_by"
    : undefined;
};

/** Whether the session may change who is involved with this young person. */
export const canAssign = (
  user: CurrentUser | undefined,
  involvements: PersonInvolvement[] | undefined,
  person?: Person,
): boolean => sessionConveys(user, involvements, person).includes("assign");

/**
 * Whether the session may read what has been done to this record.
 *
 * Read off what `sessionConveys` counts, so it is what to offer rather than
 * what will be allowed: the server decides, and refuses a request this predicts wrongly.
 */
export const canReadLog = (
  user: CurrentUser | undefined,
  involvements: PersonInvolvement[] | undefined,
  person?: Person,
): boolean => sessionConveys(user, involvements, person).includes("read_log");

/**
 * Which assessment controls to offer on the record as a whole: starting one
 * needs `start_assessment`, asking a guest to assess `involve_guest`, and
 * ending a guest's access `end_guest_access` (see `PersonAction`). What may be done to each
 * assessment is a question about that assessment; see
 * `currentAssessmentActions`.
 *
 * Read off what `sessionConveys` counts, like the rest of this module, so it
 * is what to offer rather than what will be allowed.
 */
export const assessmentActions = (
  user: CurrentUser | undefined,
  involvements: PersonInvolvement[] | undefined,
  person?: Person,
): { start: boolean; askGuest: boolean; endGuestAccess: boolean } => {
  const conveyed = sessionConveys(user, involvements, person);

  return {
    start: conveyed.includes("start_assessment"),
    askGuest: conveyed.includes("involve_guest"),
    endGuestAccess: conveyed.includes("end_guest_access"),
  };
};

/**
 * Whether to offer a guest's Details, read off what `sessionConveys` counts
 * with `mayReadGuestDetails`, so it is what to offer rather than what will be
 * allowed.
 */
export const guestDetailsOffered = (
  user: CurrentUser | undefined,
  involvements: PersonInvolvement[] | undefined,
  person?: Person,
): ((guest: GuestOnCase) => boolean) => {
  const conveyed = sessionConveys(user, involvements, person);

  return (guest) =>
    user !== undefined &&
    mayReadGuestDetails({
      conveyed,
      actorUserId: user.id,
      startedByUserId: guest.startedByUserId,
    });
};

/**
 * Whether the session may approve or return an assessment now: a definitive
 * one, submitted and standing, whose owner the session's approver involvement
 * names, and whose owner is not the signed-in user, whichever organisation
 * either acts for.
 *
 * Read off what the server's gate also reads — `approve` over the record, and
 * `isAssessmentApprover` for whose work — so it is what to offer; the server
 * still decides.
 */
export const assessmentDecision = (
  user: CurrentUser | undefined,
  involvements: PersonInvolvement[] | undefined,
  person?: Person,
): ((assessment: Assessment) => boolean) => {
  const party = actingParty(user);
  const conveyed = sessionConveys(user, involvements, person);

  return (assessment) =>
    party !== undefined &&
    assessment.definitive &&
    assessment.status === "submitted" &&
    assessment.deletion === null &&
    assessment.ownerUserId !== party.userId &&
    conveyed.includes("approve") &&
    isAssessmentApprover(involvements ?? [], party, assessment);
};

/**
 * The name of whoever an assessment is the work of, where the screen can tell:
 * the viewer's own, or that on any involvement the owner has with the young
 * person, ended ones included, since an assessment outlives the involvement
 * that let its owner start it.
 */
export const assessorNameOf = (
  assessment: Pick<Assessment, "ownerUserId">,
  viewer: CurrentUser | undefined,
  involvements: PersonInvolvement[] | undefined,
): string | undefined => {
  if (viewer?.id === assessment.ownerUserId) {
    return viewer.name;
  }

  return (involvements ?? []).find(
    ({ party }) =>
      party.kind === "user" && party.userId === assessment.ownerUserId,
  )?.party.name;
};

/**
 * The name of the user who decided a submission, from the young person's
 * `approver` involvements: ended ones included, since a decision outlives the
 * involvement that allowed it. Undefined where none names them.
 */
export const deciderNameOf = (
  involvements: PersonInvolvement[] | undefined,
  userId: UserId,
): string | undefined =>
  (involvements ?? []).find(
    ({ kind, party }) =>
      kind === "approver" && party.kind === "user" && party.userId === userId,
  )?.party.name;

/**
 * What starting the definitive assessment involves, for the young person's
 * live designated assessor acting for the organisation the designation came
 * through; undefined for anybody else, whose starts are not definitive.
 *
 * The approver is the live `approver` involvement that approves the starter's
 * work. A definitive assessment is under way from its start until it is
 * approved (`isAssessmentStatusUnderWay`) or deleted, whoever started it, since a young
 * person has one at a time; `assessments` is undefined until read, when none
 * is known.
 */
export const definitiveStart = (
  user: CurrentUser | undefined,
  involvements: PersonInvolvement[] | undefined,
  assessments: Assessment[] | undefined,
):
  | { approverName: string | undefined; definitiveUnderWay: boolean }
  | undefined => {
  const actingFor = user?.sessionContext?.organisationId;

  // Through the organisation being acted for, because a start is made for it.
  if (
    user === undefined ||
    actingFor === undefined ||
    !isPartyDesignatedAssessor(involvements ?? [], {
      userId: user.id,
      organisationId: actingFor,
    })
  ) {
    return undefined;
  }

  return {
    approverName: approverPartyForAssessor(involvements ?? [], user.id)?.name,
    definitiveUnderWay: (assessments ?? []).some(
      (assessment) =>
        assessment.definitive &&
        assessment.deletion === null &&
        isAssessmentStatusUnderWay(assessment.status),
    ),
  };
};

/**
 * Whether the session is offered deleting each assessment, as
 * `mayDeleteAssessment` allows over what `sessionConveys` counts.
 */
export const offersDelete = (
  user: CurrentUser | undefined,
  involvements: readonly PersonInvolvement[] | undefined,
  person?: Person,
): ((assessment: Assessment) => boolean) => {
  const party = actingParty(user);
  const conveyed = sessionConveys(user, involvements, person);

  return (assessment) =>
    party !== undefined &&
    mayDeleteAssessment({ actions: conveyed, party, assessment });
};

/**
 * What the session is offered on each assessment in the workflow, the union of
 * what each part it holds allows:
 *
 * - its owner continues it while it is in progress or returned, as long as
 *   the record still lets them assess; views it while it waits for a
 *   decision, as long as `mayReadAssessmentContents` lets them read it;
 * - its owner, and whoever runs the case, delete it as `mayDeleteAssessment`
 *   allows;
 * - the approver of its owner's work reviews it once submitted.
 *
 * The owner, not whoever is designated now: the server keys working on an
 * assessment to the account that started it, acting for the organisation it
 * was started for (`isAssessmentOwner`).
 *
 * Only for an assessment under way (`isAssessmentStatusUnderWay`); a finished one
 * throws.
 *
 * Read off what `sessionConveys` counts, like the rest of this module, so it
 * is what to offer rather than what will be allowed.
 */
export const currentAssessmentActions = (
  user: CurrentUser | undefined,
  involvements: PersonInvolvement[] | undefined,
  person?: Person,
): ((assessment: Assessment) => CurrentAssessmentActions) => {
  const party = actingParty(user);
  const conveyed = sessionConveys(user, involvements, person);
  const decides = assessmentDecision(user, involvements, person);
  const deletes = offersDelete(user, involvements, person);

  return (assessment) => {
    if (!isAssessmentStatusUnderWay(assessment.status)) {
      throw new Error(`A ${assessment.status} assessment is not current`);
    }

    const owns = sessionOwnsAssessment({ user, assessment });
    const inProgress = assessment.status === "open";

    const offered = {
      continue:
        owns &&
        conveyed.includes("assess") &&
        (inProgress || assessment.status === "returned"),
      view:
        owns &&
        party !== undefined &&
        assessment.status === "submitted" &&
        mayReadAssessmentContents(
          conveyed,
          involvements ?? [],
          party,
          assessment,
        ),
      review: decides(assessment),
      delete: deletes(assessment),
    };

    const offersSomething =
      offered.continue || offered.view || offered.review || offered.delete;

    return {
      ...offered,
      why: offersSomething
        ? undefined
        : match(assessment.status)
            .returnType<NoActionReason>()
            .with("open", "returned", () => "assessor_only")
            .with("submitted", () => "approver_only")
            .with("approved", "completed", () => {
              throw new Error("Unreachable: refused above");
            })
            .exhaustive(),
    };
  };
};

/**
 * What the session is offered on each finished assessment: reading it, read
 * only, wherever `mayReadAssessmentContents` lets the session read what it says.
 *
 * Read off what `sessionConveys` counts, like the rest of this module, so it
 * is what to offer rather than what will be allowed.
 */
export const finishedAssessmentActions = (
  user: CurrentUser | undefined,
  involvements: PersonInvolvement[] | undefined,
  person?: Person,
): ((
  assessment: Pick<
    FinishedAssessment,
    "ownerUserId" | "organisationId" | "status" | "definitive"
  >,
) => FinishedAssessmentActions) => {
  const party = actingParty(user);
  const conveyed = sessionConveys(user, involvements, person);

  return (assessment) =>
    party !== undefined &&
    mayReadAssessmentContents(conveyed, involvements ?? [], party, assessment)
      ? { view: true }
      : { view: false, why: "contents_withheld" };
};

/**
 * The calls that change who is involved, as the page supplies them.
 *
 * Named rather than taken as the whole api client, so this module depends on
 * the requests it makes and a test supplies those rather than a client.
 */
export type InvolvementApi = {
  findUsers: (
    query: string,
    limit: number,
  ) => Promise<{ status: number; data: unknown }>;
  findOrganisations: (request: {
    type: readonly OrganisationType[];
    query: string;
    limit: number;
  }) => Promise<{ status: number; data: unknown }>;
  start: (
    personId: PersonId,
    request: StartInvolvementsRequest,
  ) => Promise<{ status: number }>;
  end: (
    personId: PersonId,
    request: EndInvolvementsRequest,
  ) => Promise<{ status: number }>;
  allocateResponsibleUser: (
    personId: PersonId,
    request: AllocateResponsibleUserRequest,
  ) => Promise<{ status: number }>;
  replaceResponsibleUser: (
    personId: PersonId,
    request: ReplaceResponsibleUserRequest,
  ) => Promise<{ status: number }>;
};

type ManagementOptions = {
  personId: PersonId;
  user: CurrentUser | undefined;
  involvements: PersonInvolvement[] | undefined;
  /** The record, so what registering it conveys is counted; see `sessionConveys`. */
  person?: Person | undefined;
  api: InvolvementApi;
  reread: () => Promise<unknown>;
  /** Takes the reader off the record, once a change leaves them unable to read it. */
  leave: () => Promisable<void>;
};

/**
 * A write that succeeded re-reads the whole panel rather than patching in what it
 * sent, because what an involvement conveys is the server's answer. Every
 * conflict is shown as the record having changed since it was read: the one
 * refusal the person can act on, by reading it again.
 */
const outcomeAfterReread =
  (reread: () => Promise<unknown>) =>
  async (
    write: Promise<{ status: number }>,
    expected: number,
  ): Promise<InvolvementOutcome> => {
    if ((await write).status === 409) {
      return "changed";
    }

    return (await writeSucceeded(write, expected, reread)) ? "done" : "failed";
  };

/** Reads again for somebody who asked to, whose page stays as it was if that fails. */
const rereadOnRequest = (reread: () => Promise<unknown>) => async () => {
  await reread().catch(() => undefined);
};

export const partySearch = ({
  api,
  near,
}: {
  api: Pick<InvolvementApi, "findUsers" | "findOrganisations">;
  near: readonly OrganisationId[];
}): PartySearch => {
  const results = <T>(response: { status: number; data: unknown }): T[] =>
    response.status === 200 ? (response.data as { results: T[] }).results : [];

  return {
    near,
    findUsers: async (term: string): Promise<FoundUser[]> =>
      results<FoundUser>(await api.findUsers(term, PICKER_PAGE)),
    findOrganisations: async (
      types: readonly OrganisationType[],
      term: string,
    ): Promise<Organisation[]> =>
      results<Organisation>(
        await api.findOrganisations({
          type: types,
          query: term,
          limit: PICKER_PAGE,
        }),
      ),
  };
};

/**
 * Everything needed to change who is involved, or nothing at all for a reader
 * who may not.
 */
export const involvementManagement = (
  options: ManagementOptions,
): InvolvementManagement | undefined => {
  const { personId, user, involvements, person, api, reread, leave } = options;
  const acting = user?.sessionContext;

  if (
    !canAssign(user, involvements, person) ||
    acting === null ||
    acting === undefined
  ) {
    return undefined;
  }

  const outcomeOf = outcomeAfterReread(reread);

  return {
    ...partySearch({
      api,
      near: organisationsNearPerson({
        involvements: involvements ?? [],
        actingOrganisationId: acting.organisationId,
      }),
    }),
    onAdd: (request) => outcomeOf(api.start(personId, request), 201),
    onEnd: (request) => outcomeOf(api.end(personId, request), 200),
    onReplaceResponsibleUser: (request) => {
      const keepsTheCase = request.userId === user?.id;
      const readsWithoutIt = sessionConveys(
        user,
        involvements?.filter(({ id }) => id !== request.involvementId),
        person,
      ).includes("read");
      const losesTheRecord = !keepsTheCase && !readsWithoutIt;

      return outcomeAfterReread(
        losesTheRecord
          ? async () => {
              await leave();
            }
          : reread,
      )(api.replaceResponsibleUser(personId, request), 200);
    },
    onRefresh: rereadOnRequest(reread),
  };
};

/** Naming who runs a case nobody runs, for whoever may. */
export type ResponsibleUserAllocation = Simplify<
  PartySearch &
    Pick<InvolvementManagement, "onRefresh"> & {
      onAllocateResponsibleUser: (
        request: AllocateResponsibleUserRequest,
      ) => Promisable<InvolvementOutcome>;
    }
>;

/**
 * Everything needed to name who runs a case nobody runs, or nothing for a
 * reader who may not. Offered only when the server would accept it: nobody runs
 * the case and the session conveys `allocate_responsible_user`.
 */
export const responsibleUserAllocation = (
  options: ManagementOptions,
): ResponsibleUserAllocation | undefined => {
  const { personId, user, involvements, person, api, reread } = options;
  const acting = user?.sessionContext;

  if (
    involvements === undefined ||
    !sessionConveys(user, involvements, person).includes(
      "allocate_responsible_user",
    ) ||
    acting === null ||
    acting === undefined
  ) {
    return undefined;
  }

  return {
    ...partySearch({
      api,
      near: organisationsNearPerson({
        involvements,
        actingOrganisationId: acting.organisationId,
      }),
    }),
    onAllocateResponsibleUser: (request) =>
      outcomeAfterReread(reread)(
        api.allocateResponsibleUser(personId, request),
        201,
      ),
    onRefresh: rereadOnRequest(reread),
  };
};
